// @spec GHG-STR-009
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMarketInstrument } from '@/features/ghg/api/use-market-instruments';
import {
  MarketInstrumentDetailModal,
  MarketInstrumentForm,
  MarketInstrumentList,
} from '@/features/ghg/components';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/market-instruments')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.marketInstruments'),
  }),
  component: MarketInstrumentsPage,
});

/**
 * Market instruments page for managing RECs, PPAs, green tariffs, and other
 * market-based instruments used in Scope 2 emissions calculations.
 *
 * Allows GHG managers to:
 * - View list of all market instruments with filters
 * - Create new instruments (RECs, PPAs, green tariffs, etc.)
 * - View detailed instrument information
 * - Edit existing instruments
 * - Mark instruments as verified
 * - Delete instruments
 */
function MarketInstrumentsPage() {
  const { t } = useTranslation('ghg');
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingInstrumentId, setEditingInstrumentId] = useState<string | null>(null);

  // Fetch the instrument when editing
  const { instrument: editingInstrument } = useMarketInstrument(editingInstrumentId ?? '');

  const handleInstrumentClick = (id: string) => {
    setSelectedInstrumentId(id);
  };

  const handleCreateClick = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEditFromDetail = () => {
    if (selectedInstrumentId) {
      setEditingInstrumentId(selectedInstrumentId);
      setSelectedInstrumentId(null);
    }
  };

  const handleDeleteComplete = () => {
    setSelectedInstrumentId(null);
  };

  const handleFormSuccess = () => {
    setIsCreateDialogOpen(false);
    setEditingInstrumentId(null);
  };

  const handleFormCancel = () => {
    setIsCreateDialogOpen(false);
    setEditingInstrumentId(null);
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('marketInstrumentsPage.heading')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('marketInstrumentsPage.description')}
          </p>
        </header>

        <MarketInstrumentList
          onInstrumentClick={handleInstrumentClick}
          onCreateClick={handleCreateClick}
        />

        {/* Detail Modal */}
        {selectedInstrumentId && (
          <MarketInstrumentDetailModal
            instrumentId={selectedInstrumentId}
            open={!!selectedInstrumentId}
            onOpenChange={(open) => {
              if (!open) setSelectedInstrumentId(null);
            }}
            onEdit={handleEditFromDetail}
            onDelete={handleDeleteComplete}
          />
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          open={isCreateDialogOpen || !!editingInstrumentId}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreateDialogOpen(false);
              setEditingInstrumentId(null);
            }
          }}
        >
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingInstrumentId ? t('marketInstrumentsPage.editDialogTitle') : t('marketInstrumentsPage.createDialogTitle')}
              </DialogTitle>
              <DialogDescription>
                {editingInstrumentId
                  ? t('marketInstrumentsPage.editDialogDesc')
                  : t('marketInstrumentsPage.createDialogDesc')}
              </DialogDescription>
            </DialogHeader>
            <MarketInstrumentForm
              {...(editingInstrument ? { instrument: editingInstrument } : {})}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
